'use client'

import { useEffect, useId, useRef } from 'react'
import { useTurnstileConfig } from './useTurnstileConfig'

/**
 * Cloudflare Turnstile widget.
 *
 * Loads the CF script once on mount, renders an invisible (managed) widget,
 * and calls onVerify(token) when the visitor passes. The token is one-time —
 * use it in the form submit, then it's gone.
 *
 * Most users see nothing. Suspicious visitors get a small inline challenge.
 *
 * Usage:
 *   <TurnstileWidget onVerify={setToken} />
 *
 * The site key comes from /api/security/turnstile (the platform switch in
 * platform_settings, per host). When the check is off for this host the
 * component renders nothing and calls onVerify('') at once, so no form waits
 * for a token that will never come.
 */

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        opts: Record<string, unknown>
      ) => string
      remove: (widgetId: string) => void
      reset: (widgetId?: string) => void
    }
    onloadTurnstileCallback?: () => void
  }
}

type Props = {
  onVerify: (token: string) => void
  onExpire?: () => void
  onError?: () => void
  /** 'auto' | 'light' | 'dark' */
  theme?: 'auto' | 'light' | 'dark'
  /** Optional 'invisible' to fully hide unless challenge is needed */
  appearance?: 'always' | 'execute' | 'interaction-only'
  className?: string
}

let scriptInjectionPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptInjectionPromise) return scriptInjectionPromise

  scriptInjectionPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]')
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('turnstile script failed')), { once: true })
      return
    }
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.defer = true
    s.dataset.turnstile = 'true'
    s.addEventListener('load', () => resolve(), { once: true })
    s.addEventListener('error', () => reject(new Error('turnstile script failed')), { once: true })
    document.head.appendChild(s)
  })

  return scriptInjectionPromise
}

export function TurnstileWidget({
  onVerify,
  onExpire,
  onError,
  theme = 'auto',
  appearance = 'interaction-only',
  className,
}: Props) {
  const containerId = useId()
  const widgetIdRef = useRef<string | null>(null)
  // Was hardcoded null from 2026-05-07 (Cloudflare 110200 blocked tenant
  // checkouts on hosts missing from the widget's list). Now per host: the
  // server only hands out the key for hosts on platform_settings.turnstileHosts.
  const config = useTurnstileConfig()
  const siteKey = config?.siteKey ?? null
  const configLoaded = config !== undefined

  // Stash callbacks in a ref so the widget effect doesn't re-run when the
  // parent passes new inline functions on every render. Without this, the
  // widget mounts/unmounts in a loop and never settles into a verified state.
  const callbacksRef = useRef({ onVerify, onExpire, onError })
  callbacksRef.current = { onVerify, onExpire, onError }

  useEffect(() => {
    if (!configLoaded) return
    if (!siteKey) {
      // Disabled — fire onVerify immediately so the form isn't stuck
      // waiting for a token that will never arrive.
      callbacksRef.current.onVerify('')
      return
    }
    let cancelled = false
    let verified = false

    // Safety net: if the widget never verifies within 8s (CF outage,
    // hostname not in allowlist returning 110200, ad-blocker stripping
    // the script, slow connection, etc.), unblock the form by emitting
    // an empty token. The server fails-open on empty tokens with a
    // warning log — bot risk is bounded by the in-app rate limiter.
    const failopenTimer = setTimeout(() => {
      if (cancelled || verified) return
      console.warn('[turnstile] no verification within 8s, releasing form')
      callbacksRef.current.onVerify('')
    }, 8000)

    loadScript()
      .then(() => {
        if (cancelled) return
        if (!window.turnstile) return
        const el = document.getElementById(containerId)
        if (!el) return
        widgetIdRef.current = window.turnstile.render(el, {
          sitekey: siteKey,
          theme,
          appearance,
          callback: (token: string) => {
            verified = true
            callbacksRef.current.onVerify(token)
          },
          'expired-callback': () => callbacksRef.current.onExpire?.(),
          'error-callback': (errCode?: string) => {
            console.warn('[turnstile] error', errCode)
            callbacksRef.current.onError?.()
            // CF returned an error (110200, etc.). Don't strand the
            // user — let them submit with an empty token; the server
            // logs and fails open.
            if (!verified) {
              verified = true
              callbacksRef.current.onVerify('')
            }
          },
        })
      })
      .catch((err) => {
        console.warn('[turnstile] script load failed', err)
        callbacksRef.current.onError?.()
        if (!verified) {
          verified = true
          callbacksRef.current.onVerify('')
        }
      })

    return () => {
      cancelled = true
      clearTimeout(failopenTimer)
      try {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
        }
      } catch {}
      widgetIdRef.current = null
    }
  }, [configLoaded, siteKey, containerId, theme, appearance])

  if (!siteKey) return null
  return <div id={containerId} className={className} style={{ minHeight: 0 }} />
}
